import { getAllCertificates, addCertificate, updateCertificate, deleteCertificate } from './db.js';
import { escapeHtml, formatDate, getCertStatus, showNotification } from './utils.js';
import { switchTab } from './main.js';
import { getCvReadinessReport } from './cvReadiness.js';
import { getCvPhoto } from './db.js'; // if not already imported here

let editingCertId = null;
let currentImageData = null;

export async function renderDashboard() {
    const certificates = await getAllCertificates();

    let validCount = 0, expiringCount = 0, expiredCount = 0;
    certificates.forEach(cert => {
        const status = getCertStatus(cert.expiryDate);
        if (status.status === 'valid') validCount++;
        else if (status.status === 'expiring') expiringCount++;
        else if (status.status === 'expired') expiredCount++;
    });

    document.getElementById('total-count').textContent = certificates.length;
    document.getElementById('valid-count').textContent = validCount;
    document.getElementById('expiring-count').textContent = expiringCount;
    document.getElementById('expired-count').textContent = expiredCount;

    const upcomingList = document.getElementById('upcoming-list');

    // 1. Get up to 3 EXPIRED certificates (sorted by most recently expired first)
    const expiredCerts = certificates
        .filter(cert => getCertStatus(cert.expiryDate).status === 'expired')
        .sort((a, b) => new Date(b.expiryDate) - new Date(a.expiryDate)) 
        .slice(0, 3);

    // 2. Get up to 5 EXPIRING SOON certificates (sorted by earliest expiry date first)
    const expiringCerts = certificates
        .filter(cert => getCertStatus(cert.expiryDate).status === 'expiring')
        .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate)) 
        .slice(0, 5); // <-- CHANGED FROM 3 TO 5 HERE

    // Combine them: Expired first, then Expiring Soon
    const combinedCerts = [...expiredCerts, ...expiringCerts];

    if (combinedCerts.length === 0) {
        upcomingList.innerHTML = '<div class="empty-state"><div class="empty-state-icon">✅</div><p>All certificates are valid!</p></div>';
    } else {
        upcomingList.innerHTML = combinedCerts.map(cert => {
            const status = getCertStatus(cert.expiryDate);
            return `
                <div class="certificate-card ${status.status}">
                    <div class="cert-header">
                        <div class="cert-name">${escapeHtml(cert.name)}</div>
                        <div class="cert-status ${status.status}">${status.label}</div>
                    </div>
                    <div class="cert-details">
                        <div><strong>Type:</strong> ${escapeHtml(cert.certType)}</div>
                        <div><strong>Expires:</strong> ${formatDate(cert.expiryDate)}</div>
                        <div><strong>Days left:</strong> ${status.days}</div>
                    </div>
                  ${cert.image ? `<img src="${cert.image}" class="cert-image" alt="${escapeHtml(cert.name)}">` : ''}
                </div>
            `;
        }).join('');
    }

    // Ensure this function exists before calling it to prevent errors
    if (typeof renderDashboardCvReadiness === 'function') {
        await renderDashboardCvReadiness();
    }
}

async function renderDashboardCvReadiness() {
    const card = document.getElementById('dashboard-cv-readiness');
    if (!card) return;

    let savedProfile = {};
    try {
        savedProfile = JSON.parse(localStorage.getItem('seafarerCvProfile') || '{}');
    } catch (e) { /* ignore corrupt profile */ }

    const cvProfileFields = {
        photo: !!(await getCvPhoto()),
        passport: savedProfile['cv-passport'],
        seamansBook: savedProfile['cv-seamans-book'],
        nationality: savedProfile['cv-nationality'],
        availability: savedProfile['cv-availability'],
    };

    const report = await getCvReadinessReport(cvProfileFields);

    card.textContent = report.isReady
        ? '✓ CV Ready'
        : `⚠️ ${report.criticalCount} issue${report.criticalCount !== 1 ? 's' : ''} to fix`;
    card.className = report.isReady ? 'stat-card valid' : 'stat-card expired';

    // Make it clickable — jump straight to the CV Builder, which has
    // the detailed panel explaining exactly what's wrong.
    card.style.cursor = 'pointer';
    card.onclick = () => {
        window.switchTab('cv-builder');
    };
}

export async function renderCertificates(searchTerm = '') {
    const certificates = await getAllCertificates();
    const list = document.getElementById('certificate-list');

    let filtered = certificates;
    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = certificates.filter(cert =>
            cert.name.toLowerCase().includes(term) ||
            cert.certType.toLowerCase().includes(term) ||
            (cert.certNumber && cert.certNumber.toLowerCase().includes(term))
        );
    }

    if (filtered.length === 0) {
        list.innerHTML = '<div class="empty-state"><div class="empty-state-icon">📋</div><p>No certificates found</p></div>';
        return;
    }

    list.innerHTML = filtered.map(cert => {
        const status = getCertStatus(cert.expiryDate);
        return `
            <div class="certificate-card ${status.status}">
                <div class="cert-header">
                    <div class="cert-name">${escapeHtml(cert.name)}</div>
                    <div class="cert-status ${status.status}">${status.label}</div>
                </div>
                <div class="cert-details">
                    <div><strong>Type:</strong> ${escapeHtml(cert.certType)}</div>
                    ${cert.certNumber ? `<div><strong>Number:</strong> ${escapeHtml(cert.certNumber)}</div>` : ''}
                    <div><strong>Issued:</strong> ${formatDate(cert.issueDate)}</div>
                    <div><strong>Expires:</strong> ${formatDate(cert.expiryDate)}</div>
                </div>
                ${cert.notes ? `<div class="cert-notes"><strong>Notes:</strong> ${escapeHtml(cert.notes)}</div>` : ''}
                ${cert.image ? `<img src="${cert.image}" class="cert-image" alt="${escapeHtml(cert.name)}">` : ''}
                <div class="cert-actions">
                    <button class="btn-secondary" onclick="handleEdit(${cert.id})">Edit</button>
                    <button class="btn-delete" onclick="handleDelete(${cert.id})">Delete</button>
                </div>
            </div>
        `;
    }).join('');
}

async function handleEdit(id) {
    const certificates = await getAllCertificates();
    const cert = certificates.find(c => c.id === id);
    if (!cert) { showNotification('Certificate not found'); return; }

    editingCertId = id;

    document.getElementById('cert-name').value = cert.name || '';
    document.getElementById('cert-type').value = cert.certType || '';
    document.getElementById('cert-number').value = cert.certNumber || '';
    document.getElementById('issue-date').value = cert.issueDate || '';
    document.getElementById('expiry-date').value = cert.expiryDate || '';
    document.getElementById('notes').value = cert.notes || '';

    const imagePreviewContainer = document.getElementById('image-preview-container');
    const imagePreview = document.getElementById('image-preview');

    if (cert.image) {
        currentImageData = cert.image;
        imagePreview.src = cert.image;
        imagePreviewContainer.style.display = 'block';
    } else {
        currentImageData = null;
        imagePreviewContainer.style.display = 'none';
        imagePreview.src = '';
    }

    const submitBtn = document.querySelector('#certificate-form button[type="submit"]');
    if (submitBtn) submitBtn.textContent = 'Update Certificate';

    switchTab('add');
}

async function handleDelete(id) {
    if (confirm('Are you sure you want to delete this certificate?')) {
        await deleteCertificate(id);
        showNotification('Certificate deleted successfully');
        renderDashboard();
        renderCertificates();
    }
}

// Exposed for the inline onclick="" handlers rendered into innerHTML above.
window.handleEdit = handleEdit;
window.handleDelete = handleDelete;

export function initCertificates() {
    const imagePreviewContainer = document.getElementById('image-preview-container');
    const imagePreview = document.getElementById('image-preview');
    const certImageInput = document.getElementById('cert-image');
    const removeImageBtn = document.getElementById('remove-image-btn');

    document.getElementById('certificate-form').addEventListener('submit', async (e) => {
        e.preventDefault();

        const cert = {
            name: document.getElementById('cert-name').value,
            certType: document.getElementById('cert-type').value,
            certNumber: document.getElementById('cert-number').value,
            issueDate: document.getElementById('issue-date').value,
            expiryDate: document.getElementById('expiry-date').value,
            notes: document.getElementById('notes').value,
            image: currentImageData,
        };

        if (editingCertId) {
            await updateCertificate(editingCertId, cert);
            showNotification('Certificate updated successfully!');
            editingCertId = null;
        } else {
            cert.createdAt = new Date().toISOString();
            await addCertificate(cert);
            showNotification('Certificate saved successfully!');
        }

        e.target.reset();
        currentImageData = null;
        imagePreviewContainer.style.display = 'none';
        imagePreview.src = '';
        const submitBtn = document.querySelector('#certificate-form button[type="submit"]');
        if (submitBtn) submitBtn.textContent = 'Save Certificate';

        switchTab('dashboard');
        renderDashboard();
    });

    document.getElementById('cancel-btn').addEventListener('click', () => {
        document.getElementById('certificate-form').reset();
        editingCertId = null;
        currentImageData = null;
        imagePreviewContainer.style.display = 'none';
        imagePreview.src = '';
        const submitBtn = document.querySelector('#certificate-form button[type="submit"]');
        if (submitBtn) submitBtn.textContent = 'Save Certificate';
        switchTab('dashboard');
    });

    document.getElementById('search-input').addEventListener('input', (e) => {
        renderCertificates(e.target.value);
    });

    certImageInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                showNotification('Image is too large. Please select an image under 2MB.');
                certImageInput.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                currentImageData = event.target.result;
                imagePreview.src = currentImageData;
                imagePreviewContainer.style.display = 'block';
            };
            reader.readAsDataURL(file);
        }
    });

    removeImageBtn.addEventListener('click', () => {
        currentImageData = null;
        certImageInput.value = '';
        imagePreviewContainer.style.display = 'none';
        imagePreview.src = '';
    });

    // Lightbox
    const lightbox = document.getElementById('image-lightbox');
    const lightboxImg = document.getElementById('lightbox-img');
    const lightboxClose = document.querySelector('.lightbox-close');

    document.addEventListener('click', (e) => {
        if (e.target.classList.contains('cert-image')) {
            e.stopPropagation();
            lightboxImg.src = e.target.src;
            lightbox.classList.add('active');
        }
    });

    lightboxClose.addEventListener('click', () => {
        lightbox.classList.remove('active');
        lightboxImg.src = '';
    });
    lightbox.addEventListener('click', (e) => {
        if (e.target !== lightboxImg) {
            lightbox.classList.remove('active');
            lightboxImg.src = '';
        }
    });

const scanCertBtn = document.getElementById('scan-cert-btn');
const scanStatus = document.getElementById('scan-status');

if (scanCertBtn) {
    scanCertBtn.addEventListener('click', () => {
        // Reuse a hidden file input so we can trigger the camera/gallery
        // picker without duplicating the existing cert-image input.
        const tempInput = document.createElement('input');
        tempInput.type = 'file';
        tempInput.accept = 'image/*';
        tempInput.capture = 'environment'; // hints at rear camera on mobile
        tempInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            scanStatus.style.display = 'block';
            scanStatus.style.color = '#0066cc';
            scanStatus.textContent = 'Scanning certificate — this may take a moment on first use...';
            scanCertBtn.disabled = true;

            try {
                const { loadCertScanner } = await import('./certScannerLoader.js');
                const { extractCertificateFields } = await loadCertScanner();
                const result = await extractCertificateFields(file);
                console.log('Raw OCR text:', result.rawText);

                if (result.guessedName) document.getElementById('cert-name').value = result.guessedName;
                if (result.guessedCertNumber) document.getElementById('cert-number').value = result.guessedCertNumber;
                if (result.guessedIssueDate) document.getElementById('issue-date').value = result.guessedIssueDate;
                if (result.guessedExpiryDate) document.getElementById('expiry-date').value = result.guessedExpiryDate;

                scanStatus.textContent = '✓ Auto-filled — please review and correct any fields before saving.';
                scanStatus.style.color = '#155724';
            } catch (err) {
                console.error('Certificate scan failed:', err);
                scanStatus.textContent = '✗ Scan failed — please enter details manually.';
                scanStatus.style.color = '#991b1b';
            } finally {
                scanCertBtn.disabled = false;
            }
        };
        tempInput.click();
    });
  }
}
